import { html, css } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";
import { ShadowDOMElement } from "./fields.js";

export class AutocompleteVille extends ShadowDOMElement {
	static className = "AutocompleteVille";
	
	static properties = {
		nameCity: { type: String, attribute: "name-city" },
		namePostalCode: { type: String, attribute: "name-postal-code" },
		required: { 
			type: Boolean, 
			attribute: "required",
			converter: {
				fromAttribute: (value) => value === "false" ? false : true,
				toAttribute: (value) => (value ? "true" : "false"), 
			}
		},
		excludeRestricted: {
			type: Boolean,
			attribute: "exclude-restricted",
			converter: {
				fromAttribute: (value) => value === "false" ? false : true,
				toAttribute: (value) => (value ? "true" : "false"),
			}
		},
		errorMessage: { type: String, state: true },
		hasError: { type: Boolean, state: true },
		// Internal validation state - updated on selection/blur
		_isValid: { type: Boolean, state: true },
		_validationPending: { type: Boolean, state: true }
	};
	
	static get styles() {
		return [
			super.styles,
			css`
				.error-message {
					color: #dc3545;
					font-size: 0.875rem;
					margin-top: 0.25rem;
					padding-left: 25%;
				}
				.has-error input {
					border-color: #dc3545 !important;
					box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
				}
			`
		];
	}
	
    constructor() {
        super();
        this.autocompleteId = `autocomplete-${crypto.randomUUID()}`;
        this.textFieldId = `text-field-${crypto.randomUUID()}`;
		this.nameCity = "city";
		this.namePostalCode = "postal_code";
		this.required = true;
		this.excludeRestricted = true;
		this.errorMessage = "";
		this.hasError = false;
		// Validation state - assume valid until proven otherwise
		this._isValid = true;
		this._validationPending = false;
    }

	get apiUrl() {
		const baseUrl = window.APP_URL.api.communeList;
		return this.excludeRestricted ? `${baseUrl}?excludeRestricted=true` : baseUrl;
	}
	
	get validateUrl() {
		return window.APP_URL.api.communeValidate;
	}

    render () {
        return html`
            <row-wrapper class="${this.hasError ? 'has-error' : ''}">
                <autocomplete-text-field
                    id="${this.autocompleteId}"
                    label="Code postal ${this.required ? "*" : ""}"
                    api-url="${this.apiUrl}"
                    name="${this.namePostalCode}"
					?required=${this.required}
                ></autocomplete-text-field>
            </row-wrapper>

            <row-wrapper class="${this.hasError ? 'has-error' : ''}">
                <text-field id="${this.textFieldId}" label="Ville ${this.required ? "*" : ""}" name="${this.nameCity}" ?required=${this.required}></text-field>
            </row-wrapper>
			
			${this.errorMessage ? html`
				<div class="error-message">${this.errorMessage}</div>
			` : ''}
        `;
    }
	
	firstUpdated (){
		this.autocompleteTextField = this.shadowRoot.querySelector ("autocomplete-text-field");
		this.textField = this.shadowRoot.querySelector ("text-field");
		this.autocompleteTextField.onEnterHandler = this.handleEnter.bind (this);
		
		// Add blur listeners to trigger validation when user leaves the fields
		this._setupBlurValidation();
	}
	
	_setupBlurValidation() {
		// Wait for inner elements to be ready
		this.updateComplete.then(() => {
			const postalInput = this.autocompleteTextField?.shadowRoot?.querySelector("input");
			const cityInput = this.textField?.shadowRoot?.querySelector("input");
			
			if (postalInput) {
				postalInput.addEventListener("blur", () => this._triggerValidation());
			}
			if (cityInput) {
				cityInput.addEventListener("blur", () => this._triggerValidation());
			}
		});
	}
	
	/**
	 * Triggers async validation and updates internal state.
	 * Called on selection and on blur.
	 */
	_triggerValidation() {
		const postalCode = this.getPostalCode();
		const cityName = this.getCityName();
		
		// If both fields are empty and not required, consider valid
		if (!this.required && !postalCode && !cityName) {
			this._isValid = true;
			this.clearError();
			return;
		}
		
		// If either field is empty, don't validate yet (wait for both to have values)
		if (!postalCode || !cityName) {
			// Clear any previous error but don't mark as definitively valid
			this.clearError();
			this._isValid = true;
			return;
		}
		
		this._validationPending = true;
		
		const params = new URLSearchParams({
			postalCode: postalCode,
			cityName: cityName,
			checkRestricted: this.excludeRestricted.toString()
		});
		
		fetch(`${this.validateUrl}?${params}`)
			.then(response => response.json())
			.then(result => {
				this._validationPending = false;
				this._isValid = result.valid;
				
				if (result.valid) {
					this.clearError();
				} else {
					this.setError(result.errorMessage);
				}
			})
			.catch(error => {
				console.error("Error validating commune:", error);
				this._validationPending = false;
				this._isValid = false;
				this.setError("Erreur de validation. Veuillez réessayer.");
			});
	}

	handleEnter (selectedItem){
        if (selectedItem && selectedItem.name)
            this.textField.value = selectedItem.name;
		// Trigger validation when user selects from dropdown
		this._triggerValidation();
	}
	
	getPostalCode() {
		if (!this.autocompleteTextField) return "";
		const input = this.autocompleteTextField.shadowRoot?.querySelector("input");
		return input?.value || "";
	}
	
	getCityName() {
		if (!this.textField) return "";
		const input = this.textField.shadowRoot?.querySelector("input");
		return input?.value || "";
	}
	
	setError(message) {
		this.errorMessage = message;
		this.hasError = true;
		this.applyErrorStyles(true);
	}
	
	clearError() {
		this.errorMessage = "";
		this.hasError = false;
		this.applyErrorStyles(false);
	}
	
	applyErrorStyles(hasError) {
		// Apply error styles to the inner inputs
		const postalInput = this.autocompleteTextField?.shadowRoot?.querySelector("input");
		const cityInput = this.textField?.shadowRoot?.querySelector("input");
		
		if (postalInput) {
			if (hasError) {
				postalInput.style.borderColor = "#dc3545";
				postalInput.style.boxShadow = "0 0 0 0.2rem rgba(220, 53, 69, 0.25)";
			} else {
				postalInput.style.borderColor = "";
				postalInput.style.boxShadow = "";
			}
		}
		
		if (cityInput) {
			if (hasError) {
				cityInput.style.borderColor = "#dc3545";
				cityInput.style.boxShadow = "0 0 0 0.2rem rgba(220, 53, 69, 0.25)";
			} else {
				cityInput.style.borderColor = "";
				cityInput.style.boxShadow = "";
			}
		}
	}
	
	/**
	 * Synchronous validation method called by FormValidator.
	 * Always calls the backend to validate current values.
	 * @returns {boolean} true if valid, false if invalid
	 */
	validate() {
		const postalCode = this.getPostalCode();
		const cityName = this.getCityName();
		
		// If both empty and not required, valid
		if (!this.required && !postalCode && !cityName) {
			this.clearError();
			return true;
		}
		
		// If either is empty, let standard required validation handle it
		if (!postalCode || !cityName) {
			this.clearError();
			return true;
		}
		
		// Synchronous validation call using XMLHttpRequest
		try {
			const params = new URLSearchParams({
				postalCode: postalCode,
				cityName: cityName,
				checkRestricted: this.excludeRestricted.toString()
			});
			
			const xhr = new XMLHttpRequest();
			xhr.open('GET', `${this.validateUrl}?${params}`, false); // false = synchronous
			xhr.send();
			
			if (xhr.status === 200) {
				const result = JSON.parse(xhr.responseText);
				if (result.valid) {
					this.clearError();
					return true;
				} else {
					this.setError(result.errorMessage);
					return false;
				}
			} else {
				this.setError("Erreur de validation. Veuillez réessayer.");
				return false;
			}
		} catch (error) {
			console.error("Error validating commune:", error);
			this.setError("Erreur de validation. Veuillez réessayer.");
			return false;
		}
	}
	
	isTransient() {
		return false;
	}
}

customElements.define("autocomplete-ville", AutocompleteVille);
